import React, { useRef } from 'react';
import {
  Box,
  HStack,
  Text,
  IconButton,
  Image,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { FaPlay, FaPause, FaTimes } from 'react-icons/fa';
import { useAudioPlayer, useAudioTime } from '../context/AudioPlayerContext';
import { formatTime } from '../utils/format';

const SeekSlider = ({ compact }) => {
  const { seek } = useAudioPlayer();
  const { currentTime, duration } = useAudioTime();

  return (
    <Slider
      aria-label="Seek"
      value={currentTime}
      min={0}
      max={duration || 100}
      onChange={seek}
      focusThumbOnChange={false}
      size="sm"
      flex="1"
    >
      <SliderTrack bg="rgba(255,255,255,0.12)" h={compact ? '2px' : undefined}>
        <SliderFilledTrack bg="brand.500" />
      </SliderTrack>
      <SliderThumb boxSize={2.5} />
    </Slider>
  );
};

const TimeLabel = ({ showTotal = true }) => {
  const { currentTime, duration } = useAudioTime();
  return (
    <Text fontSize="xs" color="whiteAlpha.700" flexShrink={0} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {formatTime(currentTime)}{showTotal && ` / ${formatTime(duration)}`}
    </Text>
  );
};

const MiniPlayer = () => {
  const { currentEpisode: liveEpisode, isPlaying, togglePlay, closePlayer } = useAudioPlayer();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const reduceMotion = useReducedMotion();

  // Keep the last episode so the bar still shows content while sliding down on close.
  // (Always-mounted + animate-only: AnimatePresence exit-unmount is unreliable
  // with this framer-motion/React 18.2 pairing.)
  const lastEpisodeRef = useRef(null);
  if (liveEpisode) lastEpisodeRef.current = liveEpisode;
  const currentEpisode = liveEpisode || lastEpisodeRef.current;
  const isOpen = !!liveEpisode;

  if (!currentEpisode) return null;

  return (
    <motion.div
      initial={reduceMotion ? { opacity: 0 } : { y: '110%' }}
      animate={
        reduceMotion
          ? { opacity: isOpen ? 1 : 0 }
          : { y: isOpen ? 0 : '110%' }
      }
      transition={{ duration: reduceMotion ? 0 : 0.3, ease: 'easeOut' }}
      aria-hidden={!isOpen}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
          <Box
            bg="rgba(21,13,4,0.88)"
            backdropFilter="blur(12px)"
            borderTop="1px solid rgba(255,150,50,0.18)"
            position="relative"
            px={{ base: 3, md: 6 }}
            pt={isMobile ? 0 : 2}
            pb={2}
          >
            <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, brand.500, transparent)" />

            {isMobile ? (
              <>
                <Box pt={1}>
                  <SeekSlider compact />
                </Box>
                <HStack spacing={3} mt={-1}>
                  <Image
                    src={currentEpisode.imageUrl}
                    alt=""
                    boxSize="36px"
                    borderRadius="md"
                    objectFit="cover"
                    fallback={<Box boxSize="36px" borderRadius="md" bg="rgba(255,150,50,0.1)" flexShrink={0} />}
                  />
                  <Text
                    as={RouterLink}
                    to={`/episodes?search=${encodeURIComponent(currentEpisode.title)}`}
                    fontSize="sm"
                    fontWeight="semibold"
                    color="whiteAlpha.900"
                    noOfLines={1}
                    flex="1"
                    _hover={{ color: 'brand.300' }}
                  >
                    {currentEpisode.title}
                  </Text>
                  <TimeLabel showTotal={false} />
                  <IconButton
                    icon={isPlaying ? <FaPause /> : <FaPlay />}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    onClick={togglePlay}
                    colorScheme="brand"
                    size="sm"
                    isRound
                    className={isPlaying ? 'playing-pulse' : ''}
                  />
                  <IconButton
                    icon={<FaTimes />}
                    aria-label="Close player"
                    onClick={closePlayer}
                    variant="ghost"
                    color="whiteAlpha.700"
                    size="sm"
                    _hover={{ color: 'brand.300', bg: 'whiteAlpha.100' }}
                  />
                </HStack>
              </>
            ) : (
              <HStack spacing={4}>
                <Image
                  src={currentEpisode.imageUrl}
                  alt=""
                  boxSize="44px"
                  borderRadius="md"
                  objectFit="cover"
                  boxShadow={isPlaying ? '0 0 16px rgba(255,152,0,0.4)' : 'none'}
                  transition="box-shadow 0.3s"
                  fallback={<Box boxSize="44px" borderRadius="md" bg="rgba(255,150,50,0.1)" flexShrink={0} />}
                />
                <Text
                  as={RouterLink}
                  to={`/episodes?search=${encodeURIComponent(currentEpisode.title)}`}
                  fontSize="sm"
                  fontWeight="semibold"
                  color="whiteAlpha.900"
                  noOfLines={1}
                  maxW="280px"
                  _hover={{ color: 'brand.300' }}
                >
                  {currentEpisode.title}
                </Text>
                <IconButton
                  icon={isPlaying ? <FaPause /> : <FaPlay />}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  onClick={togglePlay}
                  colorScheme="brand"
                  size="md"
                  isRound
                  className={isPlaying ? 'playing-pulse' : ''}
                  boxShadow={isPlaying ? '0 0 20px rgba(255,152,0,0.5)' : 'none'}
                />
                <SeekSlider />
                <TimeLabel />
                <IconButton
                  icon={<FaTimes />}
                  aria-label="Close player"
                  onClick={closePlayer}
                  variant="ghost"
                  color="whiteAlpha.700"
                  size="sm"
                  _hover={{ color: 'brand.300', bg: 'whiteAlpha.100' }}
                />
              </HStack>
            )}
      </Box>
    </motion.div>
  );
};

export default MiniPlayer;
